import { StringInfoInput } from 'src/generated/graphql';

/**
 * Represents a single fetched string from a version snapshot.
 * Mirrors the fields relevant for delta comparison.
 */
export interface VersionString {
  stringId: string;
  sourceLocale: string;
  sourceText: string;
  contextNote?: string | null;
  inlineMarkupType?: string | null;
  messageFormatType?: string | null;
  variants: Array<{
    cxId: string;
    sourceText: string;
    contextNote?: string | null;
  }>;
}

/**
 * The computed delta between current and target version snapshots.
 */
export interface RollbackDelta {
  added: StringInfoInput[];
  edited: StringInfoInput[];
  deleted: string[];
}

/**
 * Error thrown when duplicate string IDs are detected within a single version
 * snapshot. This indicates data corruption that must not be silently discarded.
 */
export class DuplicateStringIdError extends Error {
  constructor(
    public readonly version: number,
    public readonly duplicateIds: string[],
  ) {
    super(
      `Duplicate string IDs detected in version ${version}: ${duplicateIds.join(', ')}`,
    );
    this.name = 'DuplicateStringIdError';
  }
}

/**
 * Normalizes optional string fields: null/undefined/empty-string all become
 * undefined for consistent comparison.
 */
function normalizeOptional(
  value: string | null | undefined,
): string | undefined {
  return value ? value : undefined;
}

/**
 * Deterministic, order-insensitive comparison of variant arrays.
 * Sorts by cxId then compares sourceText and contextNote.
 */
function variantsEqual(
  a: Array<{ cxId: string; sourceText: string; contextNote?: string | null }>,
  b: Array<{ cxId: string; sourceText: string; contextNote?: string | null }>,
): boolean {
  if (a.length !== b.length) return false;

  const sortByCxId = (
    x: { cxId: string; sourceText: string; contextNote?: string | null },
    y: { cxId: string; sourceText: string; contextNote?: string | null },
  ) => x.cxId.localeCompare(y.cxId);

  const sortedA = [...a].sort(sortByCxId);
  const sortedB = [...b].sort(sortByCxId);

  return sortedA.every(
    (va, i) =>
      va.cxId === sortedB[i].cxId &&
      va.sourceText === sortedB[i].sourceText &&
      normalizeOptional(va.contextNote) ===
        normalizeOptional(sortedB[i].contextNote),
  );
}

/**
 * Returns true when two strings are semantically identical across all
 * comparable source fields.
 */
function stringsEqual(current: VersionString, target: VersionString): boolean {
  return (
    current.sourceLocale === target.sourceLocale &&
    current.sourceText === target.sourceText &&
    normalizeOptional(current.contextNote) ===
      normalizeOptional(target.contextNote) &&
    normalizeOptional(current.inlineMarkupType) ===
      normalizeOptional(target.inlineMarkupType) &&
    normalizeOptional(current.messageFormatType) ===
      normalizeOptional(target.messageFormatType) &&
    variantsEqual(current.variants, target.variants)
  );
}

/**
 * Converts a VersionString into the mutation input format, using the target
 * version's values.
 */
function toStringInfoInput(s: VersionString): StringInfoInput {
  return {
    stringId: s.stringId,
    sourceLocale: s.sourceLocale,
    sourceText: s.sourceText,
    contextNote: normalizeOptional(s.contextNote),
    inlineMarkupType: normalizeOptional(s.inlineMarkupType) as
      | StringInfoInput['inlineMarkupType']
      | undefined,
    messageFormatType: normalizeOptional(s.messageFormatType) as
      | StringInfoInput['messageFormatType']
      | undefined,
    variants: s.variants.map((v) => ({
      cxId: v.cxId,
      sourceText: v.sourceText,
      contextNote: normalizeOptional(v.contextNote),
    })),
  };
}

/**
 * Builds a Map from stringId to VersionString while detecting duplicates.
 * @throws DuplicateStringIdError if any stringId appears more than once.
 */
function buildStringMap(
  strings: VersionString[],
  version: number,
): Map<string, VersionString> {
  const map = new Map<string, VersionString>();
  const duplicates: string[] = [];

  for (const s of strings) {
    if (map.has(s.stringId)) {
      duplicates.push(s.stringId);
    } else {
      map.set(s.stringId, s);
    }
  }

  if (duplicates.length > 0) {
    throw new DuplicateStringIdError(version, duplicates);
  }

  return map;
}

/**
 * Computes the real delta between the current version snapshot and target
 * version snapshot for a rollback operation.
 *
 * - target-only strings → added (new strings that rolling back would introduce)
 * - both present but differ → edited (with target values)
 * - current-only strings → deleted (strings absent in the target)
 * - identical → omitted
 *
 * @throws DuplicateStringIdError if either snapshot contains duplicate IDs
 */
export function computeRollbackDelta(
  currentStrings: VersionString[],
  targetStrings: VersionString[],
  currentVersion: number,
  targetVersion: number,
): RollbackDelta {
  const currentMap = buildStringMap(currentStrings, currentVersion);
  const targetMap = buildStringMap(targetStrings, targetVersion);

  const added: StringInfoInput[] = [];
  const edited: StringInfoInput[] = [];
  const deleted: string[] = [];

  // Strings in target: either added (not in current) or edited (different)
  for (const [stringId, targetString] of targetMap) {
    const currentString = currentMap.get(stringId);
    if (!currentString) {
      added.push(toStringInfoInput(targetString));
    } else if (!stringsEqual(currentString, targetString)) {
      edited.push(toStringInfoInput(targetString));
    }
    // identical → omit
  }

  // Strings in current but not in target → deleted
  for (const stringId of currentMap.keys()) {
    if (!targetMap.has(stringId)) {
      deleted.push(stringId);
    }
  }

  return { added, edited, deleted };
}

/**
 * Returns true when the delta represents no actual changes (all arrays empty).
 */
export function isDeltaEmpty(delta: RollbackDelta): boolean {
  return (
    delta.added.length === 0 &&
    delta.edited.length === 0 &&
    delta.deleted.length === 0
  );
}
